// Frontend language resolution for report text.
//
// Report body text is localized by the backend via the ?lang= URL path segment.
// This module covers the text the backend does not own — copy that only exists
// in white-label report variants — and is deliberately dependency-free.

export const REPORT_LANGS = ["en", "ru", "zh"] as const;

export type ReportLang = (typeof REPORT_LANGS)[number];

export const DEFAULT_REPORT_LANG: ReportLang = "en";

/**
 * Normalizes an untrusted lang value into a supported language.
 *
 * The report URL is minted outside this app (platform, native app, email), and
 * the documented casing is inconsistent ("lang=EN" vs "lang=en"), so this
 * accepts case-insensitive codes and regional variants (ru-RU, zh-CN, zh_Hans)
 * and falls back to English for anything unrecognised. All zh* variants resolve
 * to Simplified.
 *
 * Total function — never throws. Because it only ever returns one of three
 * whitelisted literals, the raw URL value can never reach the DOM or a CSS
 * selector.
 */
export const resolveReportLang = (raw?: string | null): ReportLang => {
  if (!raw) return DEFAULT_REPORT_LANG;

  const primary = raw.trim().toLowerCase().split(/[-_]/)[0];

  switch (primary) {
    case "ru":
      return "ru";
    case "zh":
      return "zh";
    case "en":
      return "en";
    default:
      return DEFAULT_REPORT_LANG;
  }
};

/**
 * Whether the backend-localized report text is Georgian.
 *
 * Georgian is not a `ReportLang`: the frontend owns no Georgian copy and the
 * white-label language picker must not offer it. It only matters to the PDF
 * export, which has to swap in a font that has Georgian glyphs (see
 * pdfFonts/georgianPdfFont.ts). The platform mints the URL with `lang=ge`;
 * the ISO code `ka` is accepted too so a correctly coded link also works.
 */
export const isGeorgianReportLang = (raw?: string | null): boolean => {
  if (!raw) return false;
  const primary = raw.trim().toLowerCase().split(/[-_]/)[0];
  return primary === "ge" || primary === "ka";
};
